import { useState, type FormEvent, type ReactNode } from 'react';
import BrandMark from './BrandMark';
import { ProblemError, RateLimitedError, api } from './api';

/** المسار الذى يفتحه رابط البريد — الرمز بعده فى `#token=` لا يصل للخادم. */
export const RESET_PATH = '/reset-password';

/**
 * يقرأ الرمز من رابط البريد ثم **يمحوه من شريط العنوان** فورًا: لا يبقى فى السجل
 * ولا فى لقطة شاشة ولا يُنسخ مع الرابط. والرمز يبقى فى ذاكرة الصفحة وحدها.
 */
export function takeResetToken(): string | null {
  if (window.location.pathname !== RESET_PATH) return null;
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  window.history.replaceState(null, '', RESET_PATH);
  return token ?? '';
}

/**
 * بطاقة شاشة الدخول نفسها. حين تحمل نموذجًا تكون **هى** النموذج كما فى `Login`:
 * تنسيق `.login-card` (عمود مرن) يقع على أبنائها المباشرين، ونموذج داخلها يكسره.
 */
function Card({
  title,
  children,
  onSubmit,
}: {
  title: string;
  children: ReactNode;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}) {
  const body = (
    <>
      <div className="logo">
        <BrandMark />
        <span className="logo-text">
          حسابات<span className="logo-dot"> • </span>نواة
        </span>
      </div>
      <h1>{title}</h1>
      {children}
    </>
  );
  return (
    <div className="login-screen">
      {onSubmit ? (
        <form className="login-card" onSubmit={(event) => void onSubmit(event)}>
          {body}
        </form>
      ) : (
        <div className="login-card">{body}</div>
      )}
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof RateLimitedError) {
    return 'محاولات كثيرة. انتظر ' + caught.retryAfterSeconds + ' ثانية ثم أعد المحاولة.';
  }
  if (caught instanceof ProblemError) {
    return caught.problem.detail ?? 'تعذر إتمام الطلب.';
  }
  return 'تعذر الاتصال بالخدمة. حاول بعد قليل.';
}

/**
 * «نسيت كلمة السر؟» — يطلب رابطًا بالبريد.
 *
 * الرد واحد لكل بريد (مسجَّل أو لا)، فالرسالة هنا لا تقول «أرسلنا» بل «إن كان
 * مسجَّلاً تصلك رسالة» — وإلا كذبت الشاشة على من أخطأ فى كتابة بريده.
 */
export function ForgotPassword({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.requestPasswordReset(email.trim().toLowerCase());
      setSent(true);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <Card title="راجع بريدك">
        <p className="success" role="status">
          لو البريد <bdi dir="ltr">{email.trim().toLowerCase()}</bdi> مسجَّل عندنا، ستصلك خلال دقائق
          رسالة فيها رابط لاختيار كلمة سر جديدة. الرابط صالح ٣٠ دقيقة ولمرة واحدة.
        </p>
        <p className="sub">
          لم تصل؟ راجع مجلد الرسائل غير المرغوبة (Spam)، أو اطلب رابطًا جديدًا بعد دقيقة.
        </p>
        <button type="button" className="primary" onClick={onBack}>
          العودة لتسجيل الدخول
        </button>
      </Card>
    );
  }

  return (
    <Card title="نسيت كلمة السر؟" onSubmit={handleSubmit}>
      <p className="sub">اكتب البريد الذى تدخل به، ونرسل لك رابطًا لاختيار كلمة سر جديدة.</p>

      <label htmlFor="reset-email">البريد الإلكترونى</label>
      <input
        id="reset-email"
        name="email"
        type="email"
        dir="ltr"
        autoComplete="username"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="primary" disabled={submitting}>
        {submitting ? 'جارى الإرسال…' : 'أرسل الرابط'}
      </button>
      <p className="login-hint">
        <button type="button" className="link" onClick={onBack}>
          العودة لتسجيل الدخول
        </button>
      </p>
    </Card>
  );
}

/**
 * صفحة رابط البريد — كلمة سر جديدة مرتين.
 *
 * النجاح لا يُدخل المستخدم: كل جلساته أُغلقت، ويدخل من جديد بكلمة السر **ثم** رمز
 * التحقق بخطوتين إن كان مفعّلاً. فالرابط وحده لا يتخطى العامل الثانى.
 */
export function ResetPassword({ token, onDone }: { token: string; onDone: () => void }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = confirm !== '' && confirm !== newPassword;
  const tooShort = newPassword !== '' && newPassword.length < 12;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (mismatch || tooShort || !newPassword) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.confirmPasswordReset({ token, newPassword });
      setNewPassword('');
      setConfirm('');
      setDone(true);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <Card title="الرابط غير مكتمل">
        <p className="sub">
          افتح الرابط كما وصلك فى البريد بالضبط، أو اطلب رابطًا جديدًا من «نسيت كلمة السر؟».
        </p>
        <button type="button" className="primary" onClick={onDone}>
          الذهاب لتسجيل الدخول
        </button>
      </Card>
    );
  }

  if (done) {
    return (
      <Card title="تم تغيير كلمة السر">
        <p className="success" role="status">
          ادخل الآن بكلمة السر الجديدة. أُغلقت كل الجلسات المفتوحة على حسابك، وإن كان التحقق بخطوتين
          مفعّلاً فسيُطلب منك رمزه كالمعتاد.
        </p>
        <button type="button" className="primary" onClick={onDone}>
          تسجيل الدخول
        </button>
      </Card>
    );
  }

  return (
    <Card title="اختر كلمة سر جديدة" onSubmit={handleSubmit}>
      <label htmlFor="new-password">كلمة السر الجديدة (١٢ حرفًا على الأقل)</label>
      <input
        id="new-password"
        name="new-password"
        type="password"
        autoComplete="new-password"
        required
        minLength={12}
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
      />
      {tooShort && <p className="error">١٢ حرفًا على الأقل.</p>}

      <label htmlFor="confirm-password">أعد كتابتها</label>
      <input
        id="confirm-password"
        name="confirm-password"
        type="password"
        autoComplete="new-password"
        required
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
      />
      {mismatch && <p className="error">الكلمتان غير متطابقتين.</p>}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="primary"
        disabled={submitting || mismatch || tooShort || !newPassword || !confirm}
      >
        {submitting ? 'جارى الحفظ…' : 'حفظ كلمة السر'}
      </button>
      <p className="login-hint">
        <button type="button" className="link" onClick={onDone}>
          العودة لتسجيل الدخول
        </button>
      </p>
    </Card>
  );
}
